import { useState } from "react";
import axios from "axios";
import { ArrowRight, Lock, Landmark, CreditCard } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { FIRM } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
    "w-full border border-slate-800 bg-transparent px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none transition-colors duration-300 focus:border-blue-500";

const Label = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {children}
    </label>
);

export default function Pay() {
    const [invoice, setInvoice] = useState("");
    const [amount, setAmount] = useState("");
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const [checkoutUrl, setCheckoutUrl] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        const value = parseFloat(amount);
        if (!invoice.trim() || !value || value <= 0) {
            setError("Please enter an invoice number and a valid amount.");
            setStatus("error");
            return;
        }
        setStatus("sending");
        setError("");
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                invoice_number: invoice.trim(),
                amount: Math.round(value * 100) / 100,
                payer_email: email || null,
                origin_url: window.location.origin,
            });
            if (window.self !== window.top) {
                const win = window.open(data.checkout_url, "_blank", "noopener");
                if (win) {
                    setStatus("idle");
                    return;
                }
                setCheckoutUrl(data.checkout_url);
                setStatus("idle");
                return;
            }
            window.location.href = data.checkout_url;
        } catch (err) {
            setStatus("error");
            setError("The secure payment page could not be opened. Please try again, or call us to pay by phone.");
        }
    };

    return (
        <>
            <PageMeta
                title="Client Payments — Nationwide Strategies LLC"
                description="Pay a Nationwide Strategies invoice securely online by card or ACH bank transfer, or by Zelle or check."
            />
            <PageHero
                testid="pay-hero"
                kicker="Client Payments"
                lines={["Pay an invoice,", "securely."]}
                lede="Online payments are processed by Stripe over an encrypted connection. Nationwide Strategies never sees or stores your card or bank details."
            />

            <section className="border-b border-slate-800/70">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
                    <Reveal className="lg:col-span-7">
                        <form onSubmit={submit} data-testid="payment-form" className="border border-slate-800 bg-[#0b0f16] p-8 sm:p-10">
                            <div className="flex items-center gap-3 border-b border-slate-800 pb-6">
                                <Lock size={14} className="text-blue-400" aria-hidden="true" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Secure checkout · Card & ACH bank transfer
                                </span>
                            </div>
                            <div className="mt-8 grid gap-6 sm:grid-cols-2">
                                <div>
                                    <Label htmlFor="pay-invoice">Invoice Number *</Label>
                                    <input id="pay-invoice" data-testid="pay-invoice-input" required maxLength={40}
                                        value={invoice} onChange={(e) => setInvoice(e.target.value)}
                                        className={inputClass} placeholder="e.g. INV-2026-014" />
                                </div>
                                <div>
                                    <Label htmlFor="pay-amount">Amount (USD) *</Label>
                                    <input id="pay-amount" data-testid="pay-amount-input" required type="number" min="1" max="50000" step="0.01"
                                        value={amount} onChange={(e) => setAmount(e.target.value)}
                                        className={inputClass} placeholder="0.00" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label htmlFor="pay-email">Email for Receipt</Label>
                                    <input id="pay-email" data-testid="pay-email-input" type="email"
                                        value={email} onChange={(e) => setEmail(e.target.value)}
                                        className={inputClass} placeholder="you@company.com" autoComplete="email" />
                                </div>
                            </div>
                            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-800 pt-8">
                                <div className="flex items-center gap-5 text-slate-500">
                                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                                        <CreditCard size={14} aria-hidden="true" /> Card
                                    </span>
                                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                                        <Landmark size={14} aria-hidden="true" /> ACH
                                    </span>
                                </div>
                                <button type="submit" data-testid="pay-submit-button" disabled={status === "sending"}
                                    className="group inline-flex items-center gap-3 bg-slate-100 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-900 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50">
                                    {status === "sending" ? "Opening Secure Checkout…" : "Continue to Payment"}
                                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </div>
                            {status === "error" && (
                                <p data-testid="pay-error-message" role="alert" className="mt-4 text-sm text-red-400">{error}</p>
                            )}
                            {checkoutUrl && (
                                <p className="mt-4 text-sm text-slate-400">
                                    Your browser blocked the new tab.{" "}
                                    <a href={checkoutUrl} data-testid="pay-checkout-fallback" className="text-blue-400 underline underline-offset-4 hover:text-blue-300">
                                        Open the secure checkout page
                                    </a>
                                </p>
                            )}
                        </form>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-5">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Other Ways to Pay</p>
                        <div className="mt-8 space-y-px border border-slate-800">
                            <div className="bg-[#0b0f16] p-7">
                                <p className="font-serif text-xl text-slate-100">Zelle</p>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    Send to <span className="font-mono text-slate-200">{FIRM.email}</span> from your
                                    banking app, and include your invoice number in the memo.
                                </p>
                            </div>
                            <div className="bg-[#0b0f16] p-7">
                                <p className="font-serif text-xl text-slate-100">Check</p>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    Payable to {FIRM.name}, mailed to {FIRM.address}. Include your invoice number on
                                    the memo line.
                                </p>
                            </div>
                            <div className="bg-[#0b0f16] p-7">
                                <p className="font-serif text-xl text-slate-100">By Phone</p>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    Call <span className="font-mono text-slate-200">{FIRM.phone}</span> during
                                    business hours and we will take payment details securely by phone.
                                </p>
                            </div>
                        </div>
                        <p className="mt-6 text-xs leading-relaxed text-slate-500">
                            A receipt is emailed automatically for online card payments. ACH bank transfers are
                            confirmed when the transfer clears, typically within a few business days.
                        </p>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
